import React, {useMemo, useState, useEffect, useRef, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {HiSearch, HiX} from 'react-icons/hi';
import {useChatService} from '../../services';
import {ChatHistory} from "./types";
import {setIsNewChat, setLastActiveChatId} from "../../redux/slices/chat";
import { AiFillPushpin } from 'react-icons/ai';

interface SearchChatInterfaceProps {
  onClose: () => void;
  onChatSelect: (chatId: number) => void;
}

const SearchChatInterface: React.FC<SearchChatInterfaceProps> = ({ 
  onClose, 
  onChatSelect
}) => {
  const dispatch = useDispatch();

  const {
    deleteChatMessageById,
    searchChatHistory,
    getChatHistoryList
  } = useChatService();
  
  const {
    chatHistoryByDate,
    pinnedChats,
    currentPage,
    hasMoreChats,
    isLoadingMore
  } = useSelector((state: any) => state.chatMessages);

  const resourcesData = useSelector((state: any) => state.resources.data);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChats, setSelectedChats] = useState<Set<number>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [hasMoreSearchResults, setHasMoreSearchResults] = useState(true);

  const searchObserverRef = useRef<HTMLDivElement>(null);
  const chatObserverRef = useRef<HTMLDivElement>(null);
  const searchObserverInstance = useRef<IntersectionObserver | null>(null);
  const chatObserverInstance = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (showPinnedOnly) return;
    let timeoutId: any;
    if (searchQuery.trim()) {
      setIsSearching(true);
      timeoutId = setTimeout(() => {
        handleSearch(searchQuery, 1);
      }, 300);
    } else {
      setSearchResults([]);
      setSearchPage(1);
      setHasMoreSearchResults(true);
      setIsSearching(false);
    }

    return () => timeoutId && clearTimeout(timeoutId);
  }, [searchQuery, showPinnedOnly]);

  const handleSearch = async (query: string, page: number, append = false) => {
    if (showPinnedOnly) return;
    if (!query.trim()) return;
    
    setIsSearching(true);
    try {
      const results = await searchChatHistory(query, page, 20, append);
      const allResults = [
        ...(results.today || []).map((chat: ChatHistory) => ({ ...chat, section: 'Today' })),
        ...(results.yesterday || []).map((chat: ChatHistory) => ({ ...chat, section: 'Yesterday' })),
        ...(results.older || []).map((chat: ChatHistory) => ({ ...chat, section: 'Older' }))
      ];
      
      if (append) {
        const existingIds = new Set(searchResults.map((c: any) => c.chat_id));
        const newResults = allResults.filter((c: any) => !existingIds.has(c.chat_id));
        setSearchResults(prev => [...prev, ...newResults]);
      } else {
        setSearchResults(allResults);
        setSearchPage(1);
      }
      
      setHasMoreSearchResults(results.hasMore);
      
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setHasMoreSearchResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleLoadMoreSearch = useCallback(() => {
    if (showPinnedOnly) return;
    if (isSearching || !hasMoreSearchResults) return;
    handleSearch(searchQuery, searchPage + 1, true);
    setSearchPage(prev => prev + 1);
  }, [isSearching, hasMoreSearchResults, searchQuery, searchPage, handleSearch, showPinnedOnly]);

  const handleLoadMoreChats = useCallback(async () => {
    if (showPinnedOnly) return;
    if (isLoadingMore || !hasMoreChats) return;
    
    try {
      await getChatHistoryList(null, {
        page: currentPage + 1,
        per_page: 20,
        append: true
      });
    } catch (error) {
      console.error('Error loading more chats:', error);
    }
  }, [isLoadingMore, hasMoreChats, getChatHistoryList, currentPage, showPinnedOnly]);
  const allChats = useMemo(() => [
    ...(chatHistoryByDate.today || []).map((chat: ChatHistory) => ({ ...chat, section: 'Today' })),
    ...(chatHistoryByDate.yesterday || []).map((chat: ChatHistory) => ({ ...chat, section: 'Yesterday' })),
    ...(chatHistoryByDate.older || []).map((chat: ChatHistory) => ({ ...chat, section: 'Older' }))
  ], [chatHistoryByDate]);

  const pinnedIdSet = useMemo(() => new Set((pinnedChats || []).map((c: any) => c.chat_id)), [pinnedChats]);

  const resourceIdToNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (resourcesData || []).forEach((r: any) => {
      const id = r?.id != null ? String(r.id) : undefined;
      if (id) {
        map.set(id, r?.name || r?.email || 'Unknown');
      }
    });
    return map;
  }, [resourcesData]);

  // Filter chats based on search query
  const filteredChats = useMemo(() => {
    if (showPinnedOnly) {
      const list = Array.isArray(pinnedChats) ? pinnedChats : [];
      const q = searchQuery.trim().toLowerCase();
      if (!q) return list;
      return list.filter((c: any) => (c?.title || '').toLowerCase().includes(q));
    }
    const baseChats = searchQuery.trim() ? searchResults : allChats;
    return baseChats;
  }, [searchResults, allChats, searchQuery, showPinnedOnly, pinnedChats]);

  // Handle selecting/deselecting a single chat
  const handleChatSelect = (chatId: number, isSelected: boolean) => {
    const newSelected = new Set(selectedChats);
    if (isSelected) {
      newSelected.add(chatId);
    } else {
      newSelected.delete(chatId);
    }
    setSelectedChats(newSelected);
  };

  // Handle selecting/deselecting all visible chats
  const handleSelectAll = () => {
    const shouldDeselectAll = selectedChats.size === filteredChats.length && filteredChats.length > 0;
    setSelectedChats(shouldDeselectAll ? new Set() : new Set(filteredChats.map(chat => chat.chat_id)));
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedChats.size === 0) return;
    
    setIsDeleting(true);
    try {
      await Promise.all(
        Array.from(selectedChats).map(chatId => deleteChatMessageById(chatId, ''))
      );

      await getChatHistoryList(null)
      
      // Clear selection
      setSelectedChats(new Set());
      
      // If all chats are deleted, close search interface
      if (selectedChats.size === filteredChats.length) {
        onClose();
      }
    } catch (error) {
      console.error('Error deleting chats:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle clicking on a chat (only if not in multi-select mode)
  const handleChatClick = (chatId: number) => {
    if (selectedChats.size === 0) {
      // Single selection mode - open the chat
      onChatSelect(chatId);
      onClose();
    } else {
      // Multi-select mode - toggle selection
      handleChatSelect(chatId, !selectedChats.has(chatId));
    }
  };

  const isAllSelected = filteredChats.length > 0 && selectedChats.size === filteredChats.length;

  useEffect(() => {
    if (showPinnedOnly) return; // do not attach observers when pinned only
    if (searchObserverRef.current && searchQuery.trim()) {
      searchObserverInstance.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMoreSearchResults && !isSearching) {
            handleLoadMoreSearch();
          }
        },
        { threshold: 0.1, rootMargin: '100px' }
      );
      searchObserverInstance.current.observe(searchObserverRef.current);
    }

    if (chatObserverRef.current && !searchQuery.trim()) {
      chatObserverInstance.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMoreChats && !isLoadingMore) {
            handleLoadMoreChats();
          }
        },
        { threshold: 0.1, rootMargin: '100px' }
      );
      chatObserverInstance.current.observe(chatObserverRef.current);
    }

    // Cleanup
    return () => {
      if (searchObserverInstance.current) {
        searchObserverInstance.current.disconnect();
      }
      if (chatObserverInstance.current) {
        chatObserverInstance.current.disconnect();
      }
    };
  }, [searchQuery, hasMoreSearchResults, isSearching, hasMoreChats, isLoadingMore, handleLoadMoreSearch, handleLoadMoreChats, showPinnedOnly]);

  const formatChatInfo = (chat: ChatHistory, resourceIdToNameMap: Map<string, string>) => {
    const date = new Date(chat.updated_at || chat.created_at);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const chatDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    let dateText = '';
    if (chatDate.getTime() === today.getTime()) {
      dateText = `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (chatDate.getTime() === yesterday.getTime()) {
        dateText = `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else {
        const diffTime = today.getTime() - chatDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays < 7) {
          dateText = date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
        } else if (date.getFullYear() === now.getFullYear()) {
          dateText = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        } else {
          dateText = date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
        }
      }
    }

    const createdByName = resourceIdToNameMap.get(String(chat.created_by)) || '';
    return (
      <>
        <span>{dateText}</span>
        {createdByName && (
          <>
            <span className="mx-2">•</span>
            <span>Created by {createdByName}</span>
          </>
        )}
      </>
    );
  };

  return (
    <div className="h-full  flex bg-white dark:bg-gray-900 flex-col">
      {/* Header */}
      <div className="">
        <div className="w-full max-w-4xl mx-auto flex items-center justify-between px-4 pt-4">
          <h1 className="text-2xl  font-semibold text-gray-900 dark:text-white">
            Chat History
          </h1>
          <button
            onClick={() => {
              onClose()
              dispatch(setIsNewChat(true));
              dispatch(setLastActiveChatId(null));
            }}
            className="flex items-center gap-2 px-2 py-1.5 pr-2.5 text-sm font-medium text-white bg-primary-light hover:bg-primary-hover focus:ring-4 focus:ring-blue-300 rounded-lg  transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New chat
          </button>
        </div>
      </div>

      {/* Search Input */}
      <div className="">
        <div className="w-full max-w-4xl mx-auto px-4 py-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <HiSearch className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-10 py-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                <HiX className="w-4 h-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" />
              </button>
            )}
          </div>
        </div>
      </div>

              {/* Selection Header */}
        {filteredChats.length > 0 && (
          <div className="z-10">
            <div className="w-full max-w-4xl  mx-auto px-4">
            <div className="w-full max-w-4xl  ] mx-auto   flex items-center justify-between px-4 py-3">
            <div className="text-sm text-gray-700  dark:text-gray-300 flex items-center gap-3">
             <div className="flex items-center gap-2 w-44 whitespace-nowrap">
              {selectedChats.size > 0 
                ? `${selectedChats.size} chat${selectedChats.size !== 1 ? 's' : ''} selected`
                : `${filteredChats.length} conversation${filteredChats.length !== 1 ? 's' : ''} found`
              }
              </div>
              <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none whitespace-nowrap shrink-0">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 dark:border-gray-600"
                  checked={showPinnedOnly}
                  onChange={(e) => setShowPinnedOnly(e.target.checked)}
                />
                <span>Pinned only</span>
              </label>
            </div>
            <div className={`flex items-center gap-2 transition-opacity duration-200 ${selectedChats.size > 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
              {!isAllSelected && (
                <button
                  onClick={handleSelectAll}
                  className="text-xs font-medium text-blue-700 hover:text-blue-800 dark:text-blue-500 dark:hover:text-blue-400"
                >
                  Select all
                </button>
              )}
              <button
                onClick={() => setSelectedChats(new Set())}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:ring-red-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-900"
              >
                {isDeleting ? (
                  <>
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </>
                )}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="flex-1 z-5 overflow-auto">
        <div className="w-full max-w-4xl mx-auto px-4 py-4">
          {filteredChats.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-12 text-center">
              <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full dark:bg-gray-700 mb-4">
                {searchQuery.trim() && !showPinnedOnly && isSearching ? (
                  <svg className="w-8 h-8 animate-spin text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-8 h-8 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                )}
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {searchQuery.trim()
                  ? (showPinnedOnly ? 'No pinned conversations found' : (isSearching ? 'Searching...' : 'No search results found'))
                  : (showPinnedOnly ? 'No pinned conversations yet' : 'No conversations yet')}
              </h3>
              
              <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm">
                {searchQuery.trim()
                  ? (showPinnedOnly
                      ? 'Try a different search to find a pinned conversation.'
                      : (isSearching ? 'Please wait while we search your chats.' : 'No results found for your query. Try different keywords.'))
                  : (showPinnedOnly
                      ? 'Your pinned conversations will appear here once you pin some.'
                      : 'Your conversation history will appear here once you start chatting.')}
              </p>

            </div>
          ) : (
            <div className="space-y-3 pl-8">
              {filteredChats.map((chat, index) => {
                const isSelected = selectedChats.has(chat.chat_id);
                
                return (
                  <div
                    key={chat.chat_id}
                    className="group relative"
                  >
                    {/* Checkbox positioned outside the left boundary */}
                    <div className={`absolute left-[-32px] top-1/2 transform -translate-y-1/2 transition-all duration-200 ${
                      selectedChats.size > 0 ? 'opacity-100 scale-100' : 'opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100'
                    }`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleChatSelect(chat.chat_id, e.target.checked);
                        }}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                    </div>
                    
                    {/* Chat container */}
                    <div
                      className={`flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-all duration-200 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 ${
                        isSelected ? 'border-blue-500 bg-blue-50 dark:border-blue-500 dark:bg-blue-900/20' : ''
                      }`}
                      onClick={() => handleChatClick(chat.chat_id)}
                    >
                      {/* Chat content */}
                      <div className="flex-1 min-w-0">
                        <h4 className={`text-sm font-medium truncate transition-colors duration-200 flex items-center gap-1 ${
                          isSelected ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white'
                        }`}>
                          {chat.title || 'Untitled conversation'}
                          {pinnedIdSet.has(chat.chat_id) && (
                            <AiFillPushpin className="w-3.5 h-3.5 text-amber-500 shrink-0" title="Pinned" />
                          )}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {formatChatInfo(chat, resourceIdToNameMap)}
                        </p>
                      </div>
                      
                      {/* Arrow indicator */}
                      <div className={`flex-shrink-0 transition-opacity duration-200 ${
                        isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}>
                        <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                );
              })}
              
             {!showPinnedOnly && searchQuery.trim() && hasMoreSearchResults && filteredChats.length > 0 && (
                <div ref={searchObserverRef} className="flex justify-center py-4">
                  {isSearching && (
                     <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
                     <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading more chats...
                  </div>
                  )}
                </div>
              )}

              {!showPinnedOnly && !searchQuery.trim() && hasMoreChats && filteredChats.length > 0 && (
                <div ref={chatObserverRef} className="flex justify-center py-4">
                  {isLoadingMore && (
                     <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
                     <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading more chats...
                  </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchChatInterface; 